// What this browser remembers between games: the most waves held at each difficulty (the map's id).
const KEY = 'inbound.best.v1';

// The game was called Goon Defense until 2026-10-19; carry what that name stored over to the new keys once.
(function migrate(): void {
  try {
    for (const [from, to] of [['goon.best.v1', KEY], ['goon.save.v1', 'inbound.save.v1'], ['goon.view.v1', 'inbound.view.v1']] as const) {
      const v = localStorage.getItem(from);
      if (v !== null) {
        if (localStorage.getItem(to) === null) localStorage.setItem(to, v);
        localStorage.removeItem(from);
      }
    }
  } catch { /* storage unavailable */ }
})();

interface Progress { best: Record<string, number> }

function load(): Progress {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Progress>;
      if (parsed && typeof parsed.best === 'object' && parsed.best) return { best: parsed.best };
    }
  } catch { /* storage unavailable or unreadable */ }
  return { best: {} };
}

/** The most waves held at a difficulty, 0 when it has not been played. */
export function bestWaves(mapId: string): number {
  const best = load().best;
  // a record kept under the id the difficulty had before it was named still counts
  const legacy = Object.entries(LEGACY_IDS).find(([, now]) => now === mapId)?.[0];
  return Math.max(0, Math.floor(Number(best[mapId]) || 0), legacy ? Math.floor(Number(best[legacy]) || 0) : 0);
}

/** Keeps a game's wave count if it beats the record. Returns whether it did. */
export function recordWaves(mapId: string, waves: number): boolean {
  const p = load();
  if (waves <= (p.best[mapId] ?? 0)) return false;
  p.best[mapId] = waves;
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* storage unavailable */ }
  return true;
}

// ---- the game in progress: its map, seed and the player's clicks, which replay to the same game ----
import { LEGACY_IDS, type TdCommand } from '@thrash/sim';

const SAVE_KEY = 'inbound.save.v3';

export interface SavedGame { mapId: string; seed: number; log: TdCommand[]; tick: number; wave: number }

export function loadSave(): SavedGame | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<SavedGame>;
    if (!s || typeof s.mapId !== 'string' || typeof s.seed !== 'number' || !Array.isArray(s.log) || typeof s.tick !== 'number') return null;
    return { mapId: s.mapId, seed: s.seed, log: s.log, tick: s.tick, wave: Number(s.wave) || 0 };
  } catch { return null; }
}

export function storeSave(s: SavedGame): void {
  try { localStorage.setItem(SAVE_KEY, JSON.stringify(s)); } catch { /* storage unavailable */ }
}

export function clearSave(): void {
  try { localStorage.removeItem(SAVE_KEY); } catch { /* storage unavailable */ }
}
